import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CustomService } from '@/shared/services/custom.service';
import { MedicoSituacaoResponse, TipoInscricaoResponse, TipoLicencaResponse, EstadoCivilResponse } from './dados-registro.model';
import { MedicoService } from '@/core/api/sicom-api/registro/medico/medico.service';
import { CensuraPublicaService } from '@/core/api/sicom-api/registro/medico/censura-publica/censura-publica.service';
import { MedicoCensuraPublicaRequest, MedicoCensuraPublicaResponse, MedicoCensuraPublicaUpdateRequest, MedicoDeficienciaResponse, MedicoPeriodoResponse } from '@/core/api/sicom-api/registro/medico/medico.model';
import { Pessoa } from '@/shared/models/pessoa-info.model';

@Injectable({
    providedIn: 'root'
})
/** Service de apoio aos dados de registro (situações e domínios do médico). */
export class DadosRegistroService extends CustomService {
    private readonly apiUrlSituacoes = environment.apis.apiSicom + '/api/v1/cadastro/medico/situacoes';
    private readonly apiUrlTiposInscricao = environment.apis.apiSicom + '/api/v1/cadastro/medico/tipos-inscricao';
    private readonly apiUrlTiposLicenca = environment.apis.apiSicom + '/api/v1/cadastro/medico/tipos-licenca';
    private readonly apiUrlEstadosCivis = environment.apis.apiSicom + '/api/v1/cadastro/medico/estados-civis';

    private readonly http = inject(HttpClient);
    private readonly medicoService = inject(MedicoService);
    private readonly censuraPublicaService = inject(CensuraPublicaService);

    /**
     * Listar períodos de suspensão do médico.
     * GET /api/v1/cadastro/medico/suspensoes
     */
    listarSuspensoes(params: Pessoa): Observable<MedicoPeriodoResponse[]> {
        return this.medicoService.listarSuspensoes(params).pipe(catchError(this.handleError));
    }

    listarDeficiencias(params: Pessoa): Observable<MedicoDeficienciaResponse[]> {
        return this.medicoService.listarDeficiencias(params).pipe(catchError(this.handleError));
    }
    /**
     * Listar censuras publicas do medico.
     * GET /api/v1/cadastro/medico/censuras-publicas
     */
    listarCensurasPublicas(params: Pessoa): Observable<MedicoCensuraPublicaResponse[]> {
        return this.censuraPublicaService.listarCensurasPublicas(params).pipe(catchError(this.handleError));
    }

    /**
     * Inserir censura publica do medico.
     * POST /api/v1/cadastro/medico/censuras-publicas
     */
    inserirCensuraPublica(body: MedicoCensuraPublicaRequest): Observable<MedicoCensuraPublicaResponse> {
        return this.censuraPublicaService.inserirCensuraPublica(body).pipe(catchError(this.handleError));
    }

    /**
     * Atualizar censura publica do medico.
     * PUT /api/v1/cadastro/medico/censuras-publicas
     */
    atualizarCensuraPublica(body: MedicoCensuraPublicaUpdateRequest): Observable<MedicoCensuraPublicaResponse> {
        return this.censuraPublicaService.atualizarCensuraPublica(body).pipe(catchError(this.handleError));
    }

    /**
     * Lista as situações de médico.
     * GET /api/v1/cadastro/medico/situacoes
     */
    listarSituacoes(): Observable<MedicoSituacaoResponse[]> {
        return this.http.get<MedicoSituacaoResponse[]>(this.apiUrlSituacoes).pipe(catchError(this.handleError));
    }

    /**
     * Retorna as situações permitidas a partir de uma situação informada, baseada nas regras de transição.
     * GET /api/v1/cadastro/medico/situacoes/{idMedSituacao}/possiveis
     */
    listarSituacoesPossiveis(idMedSituacao: string): Observable<MedicoSituacaoResponse[]> {
        return this.http.get<MedicoSituacaoResponse[]>(`${this.apiUrlSituacoes}/${idMedSituacao}/possiveis`).pipe(catchError(this.handleError));
    }

    /**
     * Lista os tipos de inscrição.
     * GET /api/v1/cadastro/medico/tipos-inscricao
     */
    listarTiposInscricao(): Observable<TipoInscricaoResponse[]> {
        return this.http.get<TipoInscricaoResponse[]>(this.apiUrlTiposInscricao).pipe(catchError(this.handleError));
    }

    /**
     * Lista os tipos de licença.
     * GET /api/v1/cadastro/medico/tipos-licenca
     */
    listarTiposLicenca(): Observable<TipoLicencaResponse[]> {
        return this.http.get<TipoLicencaResponse[]>(this.apiUrlTiposLicenca).pipe(catchError(this.handleError));
    }

    /**
     * Lista os estados civis.
     * GET /api/v1/cadastro/medico/estados-civis
     */
    listarEstadosCivis(): Observable<EstadoCivilResponse[]> {
        return this.http.get<EstadoCivilResponse[]>(this.apiUrlEstadosCivis).pipe(catchError(this.handleError));
    }
}
